<script setup>
import { ref, computed } from 'vue'
import { Card } from 'primevue'
import InputNumber from 'primevue/inputnumber'
import ANButton from '@/components/common/AnButton.vue'
import Chart from 'primevue/chart'
import { calculateRetirementFundingGap } from '@/utils/financeCalculators'

// Form data
const formData = ref({
  dateOfBirth: '1987-01-01',
  retirementAgeYears: 60,
  lifeExpectancyYears: 80,
  currentMonthlyExpense: 50000,
  currentSavings: 500000,
  retirementBenefitsLumpSum: 1000000,
  inflation: 6,
  expectedReturnWorking: 12,
  expectedReturnRetired: 8,
  expenseReductionPct: 20,

  // Advanced Monte Carlo inputs (new)
  enableMonteCarlo: false,
  retirementVolatility: 12,
  simulations: 1000,
  monteCarloStartingCorpusMode: 'requiredCorpus',
  withdrawalTiming: 'end'
})

const result = ref(null)
const chartData = ref({})
const chartOptions = ref({})

// Computed properties
const isValidInput = computed(() => {
  return formData.value.dateOfBirth &&
         formData.value.retirementAgeYears > 0 &&
         formData.value.lifeExpectancyYears > 0 &&
         formData.value.currentMonthlyExpense > 0 &&
         formData.value.inflation > 0 &&
         formData.value.expectedReturnWorking > 0 &&
         formData.value.expectedReturnRetired > 0
})

// Calculate retirement funding gap
const calculate = () => {
  if (!isValidInput.value) return

  try {
    // Map formData to calculator input (map UI 'beginning' → 'start')
    const wtRaw = formData.value.withdrawalTiming
    const wt = wtRaw === 'beginning' ? 'start' : wtRaw === 'beginning' ? 'start' : wtRaw === 'start' ? 'start' : (wtRaw === 'beginning' ? 'start' : wtRaw === 'end' ? 'end' : wtRaw)
    const input = { ...formData.value, options: { withdrawalTiming: wt } }

    result.value = calculateRetirementFundingGap(input)
    if (!result.value.error) {
      generateChart()
      generateLineChart()
      // Scroll to results section after calculation
      setTimeout(() => {
        const resultsElement = document.querySelector('.result-card')
        if (resultsElement) {
          resultsElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          })
        }
      }, 100)
    }
  } catch (error) {
    result.value = { error: 'Calculation error: ' + error.message }
  }
}

// Generate chart data
const generateChart = () => {
  if (!result.value || result.value.error) return

  const data = result.value
  chartData.value = {
    labels: ['Required Corpus', 'Current Savings (FV)', 'Retirement Benefits', 'Funding Gap'],
    datasets: [{
      label: 'Retirement Planning (₹)',
      data: [
        data.pvOfAnnuityNeededAtRetirement,
        data.fvSavingsAtRetirement,
        formData.value.retirementBenefitsLumpSum,
        Math.max(0, data.fundingGapAtRetirement)
      ],
      backgroundColor: [
        'rgba(255, 99, 132, 0.6)',   // Required - Red
        'rgba(54, 162, 235, 0.6)',   // Current Savings - Blue
        'rgba(75, 192, 192, 0.6)',   // Benefits - Teal
        'rgba(255, 206, 86, 0.6)'    // Gap - Yellow
      ],
      borderColor: [
        'rgba(255, 99, 132, 1)',
        'rgba(54, 162, 235, 1)',
        'rgba(75, 192, 192, 1)',
        'rgba(255, 206, 86, 1)'
      ],
      borderWidth: 1
    }]
  }

  chartOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Retirement Corpus Breakdown'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed
            return context.label + ': ₹' + formatCurrency(value)
          }
        }
      }
    }
  }
}

// New: Line chart data for deterministic + Monte Carlo median and band
const lineChartData = ref({})
const lineChartOptions = ref({})

const generateLineChart = () => {
  if (!result.value || result.value.error) return

  const det = result.value.deterministicRetirementProjection || []
  const mc = result.value.monteCarlo
  const years = det.map(d => d.year)

  const detSeries = det.map(d => d.remainingCorpus)
  const withdrawalSeries = det.map(d => d.annualExpense || 0)
  const medianSeries = mc && mc.yearlyPercentileBands ? mc.yearlyPercentileBands.map(b => b.p50) : []
  const p10 = mc && mc.yearlyPercentileBands ? mc.yearlyPercentileBands.map(b => b.p10) : []
  const p90 = mc && mc.yearlyPercentileBands ? mc.yearlyPercentileBands.map(b => b.p90) : []

  lineChartData.value = {
    labels: years,
    datasets: [
      {
        label: 'Deterministic (PV path)',
        data: detSeries,
        borderColor: 'rgba(15,23,42,1)',
        backgroundColor: 'rgba(15,23,42,0.05)',
        fill: false,
        tension: 0.2,
        pointRadius: 2,
        yAxisID: 'y'
      },
      {
        label: 'Annual Withdrawal',
        data: withdrawalSeries,
        borderColor: 'rgba(239,68,68,1)',
        backgroundColor: 'rgba(239,68,68,0.06)',
        borderWidth: 3,
        fill: false,
        tension: 0.2,
        pointRadius: 0,
        yAxisID: 'y2'
      },
      ...(medianSeries.length ? [{
        label: 'Median (Monte Carlo)',
        data: medianSeries,
        borderColor: 'rgba(59,130,246,1)',
        backgroundColor: 'rgba(59,130,246,0.06)',
        fill: false,
        borderDash: [6,4],
        tension: 0.2,
        pointRadius: 2,
        yAxisID: 'y'
      }] : []),
      // optional area band represented by two datasets (p90 and p10) with fill between
      ...(p90.length && p10.length ? [
        {
          label: 'P90',
          data: p90,
          borderColor: 'rgba(59,130,246,0.0)',
          backgroundColor: 'rgba(59,130,246,0.18)',
          fill: '+1',
          pointRadius: 0,
          tension: 0.2,
          yAxisID: 'y'
        },
        {
          label: 'P10',
          data: p10,
          borderColor: 'rgba(59,130,246,0.0)',
          backgroundColor: 'rgba(59,130,246,0.18)',
          fill: false,
          pointRadius: 0,
          tension: 0.2,
          yAxisID: 'y'
        }
      ] : [])
    ]
  }

  lineChartOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: { mode: 'index', intersect: false }
    },
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: { title: { display: true, text: 'Years into retirement' } },
      y: {
        title: { display: true, text: 'Corpus (₹)' },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 8,
          callback: function(value) {
            if (value >= 10000000) return (value / 10000000).toFixed(1) + 'Cr'
            if (value >= 100000) return (value / 100000).toFixed(1) + 'L'
            return value
          }
        }
      },
      y2: {
        position: 'right',
        title: { display: true, text: 'Annual Withdrawal (₹)' },
        grid: { drawOnChartArea: false },
        ticks: {
          callback: function(value) {
            if (value >= 10000000) return (value / 10000000).toFixed(1) + 'Cr'
            if (value >= 100000) return (value / 100000).toFixed(1) + 'L'
            return value
          }
        }
      }
    }
  }
}

// New: show warning/summary under projection chart
const warningText = computed(() => {
  if (!result.value || result.value.error) return ''
  const det = result.value.deterministicRetirementProjection || []
  if (!det.length) return ''
  const finalCorpus = det[det.length - 1].remainingCorpus || 0
  const finalWithdrawal = det[det.length - 1].annualExpense || 0
  const years = det[det.length - 1].year || det.length - 1
  return `With these inputs the corpus will shrink to ~₹${formatCurrency(finalCorpus)} by year ${years}; annual withdrawal is ~₹${formatCurrency(finalWithdrawal)} — stress test P10 recommended.`
})

// Year-by-year deterministic table (collapsed by default)
const showYearTable = ref(false)
const toggleYearTable = () => { showYearTable.value = !showYearTable.value }

// Explanation accordion state (was missing)
const showExplanation = ref(false)
const toggleExplanation = () => { showExplanation.value = !showExplanation.value }

// Dynamic header for withdrawal column
const withdrawalHeader = computed(() => {
  // prefer the option used in result if available, otherwise use form value
  const wtFromResult = result.value && result.value.optionsUsed && result.value.optionsUsed.withdrawalTiming
  const wt = wtFromResult || formData.value.withdrawalTiming
  if (wt === 'start' || wt === 'beginning') return 'Withdrawal (at start of year)'
  return 'Withdrawal (at end of year)'
})

// Monte Carlo survival probability (best-effort extraction)
const survivalProbabilityText = computed(() => {
  if (!formData.value.enableMonteCarlo) return ''
  const mc = result.value && result.value.monteCarlo
  if (!mc) return ''

  const tryNumber = (v) => {
    if (v == null) return null
    if (typeof v === 'number') return v
    if (typeof v === 'string') {
      const n = parseFloat(v)
      return isNaN(n) ? null : n
    }
    return null
  }

  const keys = ['survivalProbability','survivalRate','probabilityOfSurvival','successProbability','survivalPct']
  for (const k of keys) {
    const val = tryNumber(mc[k])
    if (val != null) {
      const pct = val > 1 ? val : val * 100
      return `${Math.round(pct*10)/10}% chance to survive`
    }
  }

  // try simulationResults array
  if (Array.isArray(mc.simulationResults) && mc.simulationResults.length) {
    let survived = 0
    let total = mc.simulationResults.length
    for (const sim of mc.simulationResults) {
      if (sim == null) continue
      if (typeof sim === 'object') {
        if (sim.survived === true) survived++
        else if (sim.finalCorpus != null) {
          if (Number(sim.finalCorpus) > 0) survived++
        } else if (sim.bankrupt === false) survived++
      }
    }
    if (total > 0) return `${Math.round((survived/total)*1000)/10}% chance to survive (${survived}/${total})`
  }

  // try simple counts
  if (mc.simulations && mc.survivedCount != null) {
    const s = tryNumber(mc.survivedCount)
    const t = tryNumber(mc.simulations) || mc.simulations
    if (s != null && t) return `${Math.round((s/t)*1000)/10}% chance to survive (${s}/${t})`
  }

  return ''
})

const deterministicRows = computed(() => {
  const rows = []
  if (!result.value || result.value.error) return rows
  const det = result.value.deterministicRetirementProjection || []
  if (!det.length) return rows
  const r = Number(result.value.inputs?.expectedReturnRetired ?? result.value.inputs?.expectedReturnRetired) || 0
  const withdrawalTimingUsed = (result.value.optionsUsed && result.value.optionsUsed.withdrawalTiming) || (formData.value.withdrawalTiming === 'beginning' ? 'start' : formData.value.withdrawalTiming)

  for (let i = 1; i < det.length; i++) {
    const startCorpus = det[i - 1].remainingCorpus
    const annualExpense = det[i].annualExpense || 0
    let returnEarned = 0
    let endCorpus = 0

    if (withdrawalTimingUsed === 'start') {
      const afterWithdrawal = Math.max(0, startCorpus - annualExpense)
      returnEarned = roundTo2(afterWithdrawal * r)
      endCorpus = roundTo2(afterWithdrawal * (1 + r))
    } else {
      const afterReturn = startCorpus * (1 + r)
      returnEarned = roundTo2(startCorpus * r)
      endCorpus = roundTo2(Math.max(0, afterReturn - annualExpense))
    }

    rows.push({
      year: det[i].year,
      startCorpus: roundTo2(startCorpus),
      returnEarned,
      withdrawal: roundTo2(annualExpense),
      endCorpus
    })
  }
  return rows
})

function roundTo2(x) { return Math.round((x + Number.EPSILON) * 100) / 100 }

// Format currency
const formatCurrency = (amount) => {
  if (amount >= 10000000) { // 1 crore
    return (amount / 10000000).toFixed(1) + 'Cr'
  } else if (amount >= 100000) { // 1 lakh
    return (amount / 100000).toFixed(1) + 'L'
  } else {
    return amount.toLocaleString('en-IN')
  }
}

// Format date
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Initialize
calculate()
</script>

<template>
  <div class="retirement-corpus-calculator">
    <div class="calculator-grid">
      <!-- Input Form -->
      <Card class="input-card">
        <template #title>
          <h3>Retirement Corpus Calculator Input</h3>
        </template>
        <template #content>
          <div class="form-fields">
            <!-- Personal Details Section -->
            <div class="section-title">Personal Details</div>

            <!-- Date of Birth -->
            <div class="field">
              <label for="dateOfBirth">Date of Birth</label>
              <input
                id="dateOfBirth"
                v-model="formData.dateOfBirth"
                type="date"
                class="input-field date-input"
              />
            </div>

            <!-- Retirement Age -->
            <div class="field">
              <label for="retirementAge">Retirement Age (Years)</label>
              <InputNumber
                id="retirementAge"
                v-model="formData.retirementAgeYears"
                :min="35"
                :max="65"
                :step="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Life Expectancy -->
            <div class="field">
              <label for="lifeExpectancy">Life Expectancy (Years)</label>
              <InputNumber
                id="lifeExpectancy"
                v-model="formData.lifeExpectancyYears"
                :min="65"
                :max="95"
                :step="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Financial Details Section -->
            <div class="section-title">Financial Information</div>

            <!-- Current Monthly Expenses -->
            <div class="field">
              <label for="currentExpense">Current Monthly Expenses (₹)</label>
              <InputNumber
                id="currentExpense"
                v-model="formData.currentMonthlyExpense"
                :min="5000"
                :max="1000000"
                :step="5000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Current Savings -->
            <div class="field">
              <label for="currentSavings">Current Savings/Investments (₹)</label>
              <InputNumber
                id="currentSavings"
                v-model="formData.currentSavings"
                :min="0"
                :max="100000000"
                :step="50000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Retirement Benefits -->
            <div class="field">
              <label for="retirementBenefits">Expected Retirement Benefits (₹)</label>
              <InputNumber
                id="retirementBenefits"
                v-model="formData.retirementBenefitsLumpSum"
                :min="0"
                :max="50000000"
                :step="100000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
              <small class="help-text">PF, Pension, Gratuity, etc.</small>
            </div>

            <!-- Assumptions + Monte Carlo placed in responsive two-column grid -->
            <div class="assumptions-mc-grid">
              <div class="assumptions-col">
                <div class="section-title">Assumptions</div>

                <!-- Inflation Rate -->
                <div class="field">
                  <label for="inflation">Inflation Rate (%)</label>
                  <InputNumber
                    id="inflation"
                    v-model="formData.inflation"
                    :min="1"
                    :max="15"
                    :step="0.5"
                    :minFractionDigits="1"
                    :maxFractionDigits="2"
                    showButtons
                    buttonLayout="horizontal"
                    decrementButtonClass="p-button-secondary"
                    incrementButtonClass="p-button-secondary"
                    class="input-field"
                  />
                </div>

                <!-- Expected Return - Working Years -->
                <div class="field">
                  <label for="returnWorking">Expected Return - Working Years (%)</label>
                  <InputNumber
                    id="returnWorking"
                    v-model="formData.expectedReturnWorking"
                    :min="5"
                    :max="25"
                    :step="0.5"
                    :minFractionDigits="1"
                    :maxFractionDigits="2"
                    showButtons
                    buttonLayout="horizontal"
                    decrementButtonClass="p-button-secondary"
                    incrementButtonClass="p-button-secondary"
                    class="input-field"
                  />
                </div>

                <!-- Expected Return - Retirement Years -->
                <div class="field">
                  <label for="returnRetired">Expected Return - Retirement Years (%)</label>
                  <InputNumber
                    id="returnRetired"
                    v-model="formData.expectedReturnRetired"
                    :min="4"
                    :max="15"
                    :step="0.5"
                    :minFractionDigits="1"
                    :maxFractionDigits="2"
                    showButtons
                    buttonLayout="horizontal"
                    decrementButtonClass="p-button-secondary"
                    incrementButtonClass="p-button-secondary"
                    class="input-field"
                  />
                  <small class="help-text">Conservative return during retirement</small>
                </div>

                <!-- Expense Reduction -->
                <div class="field">
                  <label for="expenseReduction">Expense Reduction in Retirement (%)</label>
                  <InputNumber
                    id="expenseReduction"
                    v-model="formData.expenseReductionPct"
                    :min="0"
                    :max="50"
                    :step="5"
                    showButtons
                    buttonLayout="horizontal"
                    decrementButtonClass="p-button-secondary"
                    incrementButtonClass="p-button-secondary"
                    class="input-field"
                  />
                  <small class="help-text">Expected reduction in expenses post-retirement</small>
                </div>
              </div>
              <div class="mc-col">
                <div class="section-title">Monte Carlo Simulation Settings</div>

                <!-- Enable Monte Carlo -->
                <div class="field">
                  <label for="enableMonteCarlo">Enable Monte Carlo Simulations</label>
                  <input
                    id="enableMonteCarlo"
                    v-model="formData.enableMonteCarlo"
                    type="checkbox"
                    class="input-field"
                  />
                </div>

                <!-- Retirement Volatility -->
                <div class="field" v-if="formData.enableMonteCarlo">
                  <label for="retirementVolatility">Retirement Volatility (%)</label>
                  <InputNumber
                    id="retirementVolatility"
                    v-model="formData.retirementVolatility"
                    :min="0"
                    :max="50"
                    :step="1"
                    showButtons
                    buttonLayout="horizontal"
                    decrementButtonClass="p-button-secondary"
                    incrementButtonClass="p-button-secondary"
                    class="input-field"
                  />
                </div>

                <!-- Number of Simulations -->
                <div class="field" v-if="formData.enableMonteCarlo">
                  <label for="simulations">Number of Simulations</label>
                  <InputNumber
                    id="simulations"
                    v-model="formData.simulations"
                    :min="100"
                    :max="5000"
                    :step="100"
                    showButtons
                    buttonLayout="horizontal"
                    decrementButtonClass="p-button-secondary"
                    incrementButtonClass="p-button-secondary"
                    class="input-field"
                  />
                </div>

                <!-- Starting Corpus Mode -->
                <div class="field" v-if="formData.enableMonteCarlo">
                  <label for="monteCarloStartingCorpusMode">Monte Carlo Starting Corpus Mode</label>
                  <select
                    id="monteCarloStartingCorpusMode"
                    v-model="formData.monteCarloStartingCorpusMode"
                    class="input-field"
                  >
                    <option value="requiredCorpus">Required Corpus</option>
                    <option value="currentSavings">Current Savings</option>
                  </select>
                </div>

                <!-- Withdrawal Timing -->
                <div class="field" v-if="formData.enableMonteCarlo">
                  <label for="withdrawalTiming">Withdrawal Timing</label>
                  <select
                    id="withdrawalTiming"
                    v-model="formData.withdrawalTiming"
                    class="input-field"
                  >
                    <option value="end">End of Year</option>
                    <option value="beginning">Beginning of Year</option>
                  </select>
                </div>

                <small class="help-text">Monte Carlo runs may increase calculation time — results will show survival probability on the projection chart.</small>
              </div>

              <!-- Calculate area placed below assumptions column via grid-area -->
              <div class="calculate-area">
                <ANButton
                  label="Calculate"
                  severity="primary"
                  size="large"
                  @click="calculate"
                  :disabled="!isValidInput"
                  class="calculate-btn"
                />
              </div>


            </div>
          </div>
        </template>
      </Card>

      <!-- Results -->
      <Card v-if="result && !result.error" class="result-card">
        <template #title>
          <h3>Retirement Planning Results</h3>
        </template>
        <template #content>
          <div class="results">
            <!-- Main Result -->
            <div class="result-item highlight">
              <div class="result-label">
                {{ result.hasSurplus ? 'Surplus Fund' : 'Funding Gap' }}
              </div>
              <div class="result-value" :class="{ surplus: result.hasSurplus, gap: !result.hasSurplus }">
                ₹{{ formatCurrency(Math.abs(result.fundingGapAtRetirement)) }}
              </div>
              <div class="result-subtitle">
                @ the time of retirement ({{ formatDate(result.retirementDate) }})
              </div>
            </div>

            <!-- Summary Information -->
            <div class="result-summary">
              <div class="summary-item">
                <span class="summary-label">Current Age:</span>
                <span class="summary-value">{{ result.currentAgeYears }} years</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Years to Retirement:</span>
                <span class="summary-value">{{ result.yearsToRetirement }} years</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Years in Retirement:</span>
                <span class="summary-value">{{ result.yearsInRetirement }} years</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Required Corpus:</span>
                <span class="summary-value">₹{{ formatCurrency(result.pvOfAnnuityNeededAtRetirement) }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Available Funds:</span>
                <span class="summary-value">₹{{ formatCurrency(result.totalFundsAtRetirement) }}</span>
              </div>
            </div>

            <!-- Monthly Expenses Breakdown -->
            <div class="expense-breakdown">
              <h4>Monthly Expenses</h4>
              <div class="breakdown-grid">
                <div class="breakdown-item">
                  <div class="breakdown-label">Current</div>
                  <div class="breakdown-value">₹{{ formData.currentMonthlyExpense.toLocaleString('en-IN') }}</div>
                </div>
                <div class="breakdown-item">
                  <div class="breakdown-label">At Retirement</div>
                  <div class="breakdown-value">₹{{ formatCurrency(result.monthlyExpensesAfterReduction) }}</div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </Card>
    </div>

    <!-- Chart -->
    <Card v-if="result && !result.error && chartData.labels" class="chart-card">
      <template #title>
        <h3>Retirement Corpus Breakdown</h3>
      </template>
      <template #content>
        <div class="chart-container">
          <Chart type="doughnut" :data="chartData" :options="chartOptions" class="chart" />
        </div>
      </template>
    </Card>

    <!-- Line Chart - Deterministic + Monte Carlo -->
    <Card v-if="result && !result.error && lineChartData.labels" class="chart-card">
      <template #title>
        <h3>Retirement Corpus Projection</h3>
        <span v-if="formData.enableMonteCarlo" class="survival-probability-badge">{{ survivalProbabilityText || 'Monte Carlo: calculating...' }}</span>
      </template>
      <template #content>
        <div class="chart-container">
          <Chart type="line" :data="lineChartData" :options="lineChartOptions" class="chart" />
        </div>
      </template>
    </Card>

    <!-- Explanation Accordion -->
    <Card class="explanation-card">
      <template #title>
        <div class="accordion-header" @click="toggleExplanation">
          <h3 class="accordion-title">📊 Retirement Planning Explanation</h3>
          <i class="pi" :class="showExplanation ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
        </div>
      </template>
      <template #content>
        <div class="accordion-content" v-show="showExplanation">
          <div style="font-size:13px;line-height:1.45">
            <div><strong>📊 How to read this chart</strong></div>
            <ul style="padding-left:16px;margin:8px 0 6px 0;">
              <li><strong>Deterministic (Black):</strong> fixed-return base plan — idealised. e.g., 8% every year, no ups/downs.</li>
              <li><strong>Median (Blue):</strong> most likely outcome — half the sims do better, half worse.</li>
              <li><strong>P90 (Green):</strong> top 10% (good outcomes).</li>
              <li><strong>P10 (Yellow):</strong> bottom 10% (stress cases) — key test: does your plan survive?</li>
            </ul>

            <div style="margin-top:6px;font-weight:600">One-line: Deterministic = perfect world · Median = normal world · P90 = good · P10 = difficult</div>
            <div style="margin-top:6px;font-style:italic">Analogy: Deterministic = saying it will be 25°C every day. Monte Carlo = showing range 20–30°C.</div>

            <div style="margin-top:8px;padding:8px;border-left:4px solid #f59e0b;background:#fff7ed;font-size:13px">
              <strong>⚠️ Key insight:</strong>
              <div style="margin-top:6px">We do NOT plan for P90. Ensure the plan survives P10 (stress case).</div>
            </div>

            <!-- Monte Carlo explanation (added) -->
            <div style="margin-top:16px">
              <h4 style="margin:0 0 8px 0">✅ What a REAL Monte Carlo would do</h4>
              <p style="margin:6px 0">If you had:</p>
              <ul style="margin:6px 0 8px 20px">
                <li>Expected return = 8%</li>
                <li>Volatility = 10%</li>
              </ul>

              <p style="margin:6px 0">Then each year's return is generated using:</p>
              <div style="font-weight:600;margin:6px 0">Return = μ + σ × Z</div>

              <p style="margin:6px 0">Where:</p>
              <ul style="margin:6px 0 12px 20px">
                <li>μ = 8% (mean return)</li>
                <li>σ = 10% (volatility)</li>
                <li>Z = random number from standard normal distribution</li>
              </ul>

              <h5 style="margin:8px 0">Example</h5>
              <table class="mc-example-table">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Z value</th>
                    <th>Return calculation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1</td>
                    <td>+0.7</td>
                    <td>8% + (10% × 0.7) = 15%</td>
                  </tr>
                  <tr>
                    <td>2</td>
                    <td>-0.5</td>
                    <td>8% + (10% × -0.5) = 3%</td>
                  </tr>
                  <tr>
                    <td>3</td>
                    <td>-1.5</td>
                    <td>8% + (10% × -1.5) = -7%</td>
                  </tr>
                  <tr>
                    <td>4</td>
                    <td>+1.0</td>
                    <td>8% + (10% × 1.0) = 18%</td>
                  </tr>
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </template>
    </Card>

    <!-- Year-by-Year Deterministic Details (separate accordion) -->
    <Card v-if="deterministicRows.length" class="year-table-card">
      <template #title>
        <div class="accordion-header table-header" @click="toggleYearTable">
          <h3 class="accordion-title">🗓 Year-by-Year Deterministic Details</h3>
          <i class="pi" :class="showYearTable ? 'pi-chevron-up' : 'pi-chevron-down'" />
        </div>
      </template>
      <template #content>
        <transition name="fade">
          <div v-if="showYearTable" class="year-table-content">
            <div class="warning-text" v-if="warningText">{{ warningText }}</div>
            <table class="year-details-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th class="number">Start Corpus</th>
                  <th class="number">Return Earned</th>
                  <th class="number">{{ withdrawalHeader }}</th>
                  <th class="number">End Corpus</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in deterministicRows" :key="row.year">
                  <td>{{ row.year }}</td>
                  <td class="number">₹{{ formatCurrency(row.startCorpus) }}</td>
                  <td class="number">₹{{ formatCurrency(row.returnEarned) }}</td>
                  <td class="number">₹{{ formatCurrency(row.withdrawal) }}</td>
                  <td class="number">₹{{ formatCurrency(row.endCorpus) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </transition>
      </template>
    </Card>

    <!-- Planning Tips -->
    <Card class="tips-card">
      <template #title>
        <h3>💡 Retirement Planning Tips</h3>
      </template>
      <template #content>
        <div class="tips-grid">
          <div class="tip-item">
            <div class="tip-icon">⏰</div>
            <div class="tip-content">
              <h4>Start Early</h4>
              <p>The earlier you start, the more time your money has to compound and grow.</p>
            </div>
          </div>
          <div class="tip-item">
            <div class="tip-icon">📊</div>
            <div class="tip-content">
              <h4>Review Regularly</h4>
              <p>Review your retirement plan annually and adjust based on life changes.</p>
            </div>
          </div>
          <div class="tip-item">
            <div class="tip-icon">💰</div>
            <div class="tip-content">
              <h4>Increase Savings Rate</h4>
              <p>Gradually increase your savings rate with salary increments and bonuses.</p>
            </div>
          </div>
          <div class="tip-item">
            <div class="tip-icon">🎯</div>
            <div class="tip-content">
              <h4>Conservative Approach</h4>
              <p>Use conservative return estimates for retirement years to be safe.</p>
            </div>
          </div>
        </div>
      </template>
    </Card>

    <!-- Error Display -->
    <Card v-if="result && result.error" class="error-card">
      <template #content>
        <div class="error-message">
          <i class="pi pi-exclamation-triangle"></i>
          <span>{{ result.error }}</span>
        </div>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.retirement-corpus-calculator {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.calculator-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.section-title {
  font-weight: 700;
  color: var(--an-primary);
  font-size: 16px;
  margin-top: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--an-primary);
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  font-weight: 600;
  color: var(--an-text-primary);
  font-size: 14px;
}

.help-text {
  color: var(--an-text-secondary);
  font-size: 12px;
  font-style: italic;
}

.input-field {
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.input-field:focus {
  outline: none;
  border-color: var(--an-primary);
}

.date-input {
  font-family: inherit;
}

.calculate-btn {
  width: 100%;
  margin-top: 16px;
}

.results {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.result-item {
  text-align: center;
  padding: 20px;
  border-radius: 8px;
  background: var(--an-background-light);
}

.result-item.highlight {
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
}

.result-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  opacity: 0.9;
}

.result-value {
  font-size: 2rem;
  font-weight: 700;
}

.result-value.surplus {
  color: var(--an-success);
}

.result-value.gap {
  color: var(--an-error);
}

.result-item.highlight .result-value {
  color: inherit;
}

.result-subtitle {
  font-size: 0.9rem;
  color: var(--an-text-secondary);
  font-weight: 500;
  margin-top: 8px;
}

.result-item.highlight .result-subtitle {
  color: inherit;
  opacity: 0.9;
}

.result-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid var(--an-gray3);
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-label {
  color: var(--an-text-secondary);
  font-weight: 500;
}

.summary-value {
  font-weight: 600;
  color: var(--an-text-primary);
}

.expense-breakdown h4 {
  margin: 0 0 16px 0;
  color: var(--an-text-primary);
  font-size: 16px;
}

.breakdown-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.breakdown-item {
  text-align: center;
  padding: 16px;
  background: var(--an-background-light);
  border-radius: 8px;
  border: 1px solid var(--an-gray3);
}

.breakdown-label {
  font-size: 12px;
  color: var(--an-text-secondary);
  margin-bottom: 4px;
  font-weight: 500;
}

.breakdown-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--an-text-primary);
}

.chart-container {
  height: 400px;
  width: 100%;
}

.chart {
  height: 100% !important;
  width: 100% !important;
}

.tips-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.tip-item {
  display: flex;
  gap: 16px;
  padding: 20px;
  border-radius: 8px;
  background: var(--an-background-light);
  border-left: 4px solid var(--an-primary);
}

.tip-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.tip-content h4 {
  margin: 0 0 8px 0;
  color: var(--an-text-primary);
  font-size: 16px;
}

.tip-content p {
  margin: 0;
  color: var(--an-text-secondary);
  line-height: 1.5;
  font-size: 14px;
}

.error-card {
  background: var(--an-error-light);
  border: 1px solid var(--an-error);
}

.error-message {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--an-error);
  font-weight: 500;
}

.accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 12px 16px;
  background: var(--an-background);
  border: 1px solid var(--an-gray3);
  border-radius: 6px;
  transition: background 0.3s ease;
}

.accordion-header:hover {
  background: var(--an-background-hover);
}

.accordion-title {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--an-text-primary);
}

.pi-chevron-up,
.pi-chevron-down {
  font-size: 1rem;
  color: var(--an-text-primary);
}

.accordion-content {
  padding: 16px;
  border-top: 1px solid var(--an-gray3);
  color: var(--an-text-primary);
  font-size: 14px;
  line-height: 1.6;
}

/* add styles for year table */
.year-table-card {
  margin-top: 8px;
}
.year-details-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.year-details-table thead th {
  text-align: center;
  padding: 8px 10px;
  border-bottom: 1px solid var(--an-gray3);
  font-weight: 700;
}
.year-details-table tbody td {
  text-align: center;
  padding: 10px;
  border-bottom: 1px solid #f3f4f6;
}
.year-details-table tbody tr:nth-child(odd) {
  background: rgba(15,23,42,0.02);
}
.year-details-table .number {
  text-align: center;
}
.table-header { padding: 8px 12px }
.year-table-content { padding: 8px 0; overflow-x: auto; }
.warning-text { font-size: 13px; color: #b45309; margin-bottom: 8px }

/* New styles for survival probability badge */
.survival-probability-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 4px 8px;
  border-radius: 12px;
  background: rgba(59, 130, 246, 0.1);
  color: rgba(59, 130, 246, 1);
  font-size: 12px;
  font-weight: 500;
}

/* styles for Monte Carlo example table */
.mc-example-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
  font-size: 13px;
}
.mc-example-table thead th {
  text-align: left;
  padding: 8px 10px;
  border-bottom: 1px solid var(--an-gray3);
  font-weight: 700;
}
.mc-example-table tbody td {
  padding: 8px 10px;
  border-bottom: 1px solid #f3f4f6;
}

/* Responsive Design */
@media (max-width: 768px) {
  .calculator-grid {
    grid-template-columns: 1fr;
  }

  .breakdown-grid {
    grid-template-columns: 1fr;
  }

  .result-value {
    font-size: 1.5rem;
  }

  .chart-container {
    height: 300px;
  }

  .tips-grid {
    grid-template-columns: 1fr;
  }

  .assumptions-mc-grid {
    grid-template-columns: 1fr;
    grid-template-areas:
      "assumptions"
      "mc"
      "calculate";
  }
}

@media (max-width: 600px) {
  .year-details-table {
    font-size: 12px;
    min-width: 520px;
  }
  .year-details-table thead th,
  .year-details-table tbody td {
    padding: 8px 6px;
  }
  .accordion-header, .table-header {
    padding: 8px 10px;
  }
}
</style>
